'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { z } from 'zod';

import { AnyObject } from '@sola/schema/dotcom/helpers.js';
import { getListSearchResultsQuery } from '@sola/skippy-search-sdk/lib/queries.js';
import { QueryResponsePublished } from '@sola/skippy-search-sdk/lib/schema.js';
import { trackError } from '@sola/tracking/components/datalayer.js';

export const parseUrlParams = ({
  searchParams,
  schema,
}: {
  searchParams: URLSearchParams;
  schema: z.ZodSchema;
}) => {
  const plainSearchParams = Object.fromEntries(searchParams.entries());
  try {
    const validatedParams = schema.parse(plainSearchParams);
    return new URLSearchParams(validatedParams as AnyObject);
  } catch (error) {
    if (error instanceof z.ZodError) {
      trackError(error.message);
      console.error('Validation error:', error.issues);
    } else {
      trackError(error as string);
      console.error('Unexpected error:', error);
    }
  }
};

export const getLegacyQuery = () => {
  if (typeof window !== 'undefined' && window.location.hash) {
    return window.location.hash
      .replace(/^#/, '')
      .split('&')
      .find((segment) => segment.startsWith('stq='))
      ?.replace('stq=', '');
  }
};

const getParsedParams = (params: URLSearchParams, schema: z.ZodSchema) => {
  return parseUrlParams({
    searchParams: new URLSearchParams(params.toString()),
    schema,
  });
};

export const useUrlState = (schema: z.ZodSchema) => {
  const pathname = usePathname();
  const searchParams = getParsedParams(useSearchParams(), schema);
  const router = useRouter();

  const filters = {
    searchQuery: searchParams?.get('q') || '',
    currentPage: Number(searchParams?.get('page')) || 0,
  };

  const updateSearchParams = useCallback(
    (updatedParams: Record<string, string | undefined>) => {
      Object.entries(updatedParams).forEach(([key, value]) => {
        if (value) {
          searchParams?.set(key, value);
        } else {
          searchParams?.delete(key);
        }
      });

      const queryString = searchParams?.toString();
      router.replace(queryString ? `${pathname}?${queryString}` : pathname, {
        scroll: false,
      });
    },
    [pathname, searchParams?.toString()],
  );

  return {
    searchParams,
    filters,
    updateSearchParams,
  };
};

export const useSearchAPI = (
  searchQuery: string,
  itemsPerPage: number,
  searchParams?: URLSearchParams,
) => {
  const [searchResultsList, setSearchResultsList] =
    useState<QueryResponsePublished>();
  const [queryError, setQueryError] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const getAllResults = async () => {
      try {
        setIsLoading(true);
        const results = await getListSearchResultsQuery({
          perPage: itemsPerPage,
          offset: Number(searchParams?.get('page')) * itemsPerPage,
          query: searchQuery,
        });
        setSearchResultsList(results);
        setQueryError(null); // Clear any previous errors
        setIsLoading(false);
      } catch (error) {
        setQueryError(error);
        setIsLoading(false);
      }
    };

    getAllResults();
  }, [
    searchQuery,
    itemsPerPage,
    searchParams?.get('page'),
    searchParams?.get('q'),
  ]);

  return { list: searchResultsList, queryError, isLoading };
};
